import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ButtonGroup, Drawer } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { FormDetailsFields } from '../../../../components/form-details-fields'
import { FormDetailsValues } from '../../../../components/form-details-fields/schema'
import { useUpdateAdminForm } from '../../../../api/forms'
import { useUpdateAdminFormVersion } from '../../../../api/form-versions'
import { AdminFormResponse, AdminFormVersionResponse } from '../../../../api/types'

dayjs.extend(customParseFormat)

interface EditDetailsDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  form: AdminFormResponse
  version: AdminFormVersionResponse
  onSaved: () => void
}

/**
 * Edits the current form + version details (the values set at creation). Prefills from the already-loaded
 * form/version, saves both with PUTs in parallel, then triggers a builder refresh. No version switching —
 * that (and multi-version management) belongs with the deferred versioning epic.
 */
export const EditDetailsDrawer: React.FC<EditDetailsDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  form,
  version,
  onSaved,
}) => {
  const formId = 'edit-form-details'
  const [updateForm, savingForm] = useUpdateAdminForm(formReferenceID)
  const [updateVersion, savingVersion] = useUpdateAdminFormVersion(formReferenceID, versionReferenceID)
  const isBusy = savingForm || savingVersion

  const initialValues: Partial<FormDetailsValues> = useMemo(
    () => ({
      name: form.name,
      fullName: form.fullName ?? '',
      formType: form.formType,
      region: form.region === 'NONE' ? '' : form.region,
      country: form.country,
      version: version.version,
      validFromUTC: version.validFromUTC ? dayjs(version.validFromUTC).format('DD/MM/YYYY') : '',
      noticesOnlyForPrimary: version.noticesOnlyForPrimary,
      allowEmailNotices: version.allowEmailNotices,
      allowFacsimileNotices: version.allowFacsimileNotices,
      allowTextMessageNotices: version.allowTextMessageNotices,
      allowRepresenteeAddress: version.allowRepresenteeAddress,
      allowRecipientAddress: version.allowRecipientAddress,
      recipientAddressDefaultToProperty: version.recipientAddressDefaultToProperty,
      allowRecipientEmergencyContacts: version.allowRecipientEmergencyContacts,
      allowRepresenteeName2: version.allowRepresenteeName2,
      ownerContactDetailsDefaultToAgency: version.ownerContactDetailsDefaultToAgency,
      showOwnerContactDetailsForRecipient: version.showOwnerContactDetailsForRecipient,
      allowRepresenteeSigning: version.allowRepresenteeSigning,
    }),
    [form, version],
  )

  const handleClose = () => onOpenChange(false)

  const handleSubmit = async (values: FormDetailsValues) => {
    const { name, fullName, formType, region, country, version: versionLabel, validFromUTC, ...flags } = values
    const parsedValidFrom = dayjs(validFromUTC, 'DD/MM/YYYY', true)

    try {
      await Promise.all([
        updateForm({ name, fullName: fullName || null, formType, region: region || 'NONE', country }),
        updateVersion({
          version: versionLabel,
          // Falls through to the raw value when it's not DD/MM/YYYY — covers the unedited case where
          // validFromUTC is already an ISO string from the server, without risking a Date() parse crash.
          validFromUTC: parsedValidFrom.isValid() ? parsedValidFrom.toISOString() : validFromUTC,
          ...flags,
        }),
      ])
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors are surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer isOpen={isOpen} title="Edit Form Details" onClose={handleClose} footerItems={footer}>
      {isOpen && <FormDetailsFields formId={formId} onSubmit={handleSubmit} initialValues={initialValues} />}
    </Drawer>
  )
}
